import { mintPreviewToken } from './api';

/*
 * Preview links for drafts and pending revisions.
 *
 * The sites show a draft, or a pending revision in place of the live page, only
 * to a visitor holding a signed preview token for that site (?preview=<token>).
 * The console mints one per site and keeps it in memory until it is close to
 * expiry; it is never stored. Two places use it:
 *   - the "Preview" button opens the page on the site's real host in a new
 *     tab. That is first-party there, so the renderer's preview cookie works
 *     and every link followed stays in preview until "Exit preview".
 *   - the in-console iframe on the dev/appspot renderer (?domain_id=) carries
 *     &preview=<token> too: appspot no longer shows drafts without one.
 */

const cache = new Map(); // domainId -> { token, exp, host }
const MARGIN_S = 10 * 60; // re-mint when less than this is left

const nowS = () => Math.floor(Date.now() / 1000);

/* -> { token, exp, host } for the site, minted (12h) when none is cached. */
export async function getPreviewToken(domainId) {
  const hit = cache.get(domainId);
  if (hit && hit.exp - nowS() > MARGIN_S) return hit;
  const res = await mintPreviewToken(domainId);
  const entry = { token: res.token, exp: res.exp, host: res.host };
  cache.set(domainId, entry);
  return entry;
}

export function clearPreviewTokens() {
  cache.clear();
}

/* The page's path on a site: stored ids encode "/" as %2F. */
const pagePath = (slug) => String(slug || '').replace(/%2F/gi, '/').replace(/^\/+|\/+$/g, '');

/* https://<host>/<slug>/?preview=<token> on the site's real host. */
export function sitePreviewUrl(host, slug, token) {
  const path = pagePath(slug);
  return `https://${host}/${path}${path ? '/' : ''}?preview=${encodeURIComponent(token)}`;
}

/* The dev/appspot renderer URL, with the token when there is one. */
export function devPreviewUrl(previewHost, domainId, slug, token) {
  const base = `${previewHost}/${slug || ''}${slug ? '/' : ''}?domain_id=${encodeURIComponent(domainId)}`;
  return token ? `${base}&preview=${encodeURIComponent(token)}` : base;
}

/*
 * Open the page in preview on the site's real host, in a new tab.
 * The tab is opened synchronously (inside the click) so it is not treated as
 * a popup, then sent to the preview URL once the token is in hand.
 */
export async function openSitePreview(domainId, slug) {
  const tab = window.open('', '_blank');
  try {
    const { token, host } = await getPreviewToken(domainId);
    if (!host) throw new Error('This site has no hostname to preview on.');
    const url = sitePreviewUrl(host, slug, token);
    if (tab) {
      tab.opener = null;
      tab.location.href = url;
    } else {
      window.open(url, '_blank', 'noopener');
    }
    return url;
  } catch (err) {
    if (tab) tab.close();
    throw err;
  }
}

/* Same, but on the dev/appspot renderer (?domain_id=), for links that already
   pointed there. Without a token it still opens, on the published page. */
export async function openDevPreview(previewHost, domainId, slug) {
  const tab = window.open('', '_blank');
  let token = null;
  try {
    ({ token } = await getPreviewToken(domainId));
  } catch (err) {
    console.warn('No preview token; opening the published page:', err?.message || err);
  }
  const url = devPreviewUrl(previewHost, domainId, slug, token);
  if (tab) {
    tab.opener = null;
    tab.location.href = url;
  } else {
    window.open(url, '_blank', 'noopener');
  }
  return url;
}
